import { BreadcrumbContext, NavMenuContext } from '@Components/UI/Layout/Layout';
import RoutedTabs from '@Components/UI/Tabs/Tabs';
import { RouteGroup } from '@Constants/routes';
import {
	resetCurrentCourse,
	setCurrentCourse,
} from '@Features/classrooms/classroomSlice';
import { useTypedSelector } from '@Features/store';
import EnrollmentStudents from '@Pages/ClassRooms/Pages/SingleCourse/Pages/Users/EnrollmentStudent';
import { CourseContext } from '@Pages/ClassRooms/Pages/SingleCourse/Context/CourseContext';
import { Test } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/Pages/Test/Test';
import CourseContent from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/CourseContent';
import FilesTab from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/FilesTab/FilesTab';
import ReportsOverview from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseReports/ReportsOverview';
import StandaloneLectureRulesPaths from './StandaloneLectureRulesPaths';
import StandaloneLectureAnalytics from './StandaloneLectureAnalytics';
import { FormContainerStyle } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Form.Styles';
import {
	SpanStyle,
	TitleStyled,
} from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Form.Styles';
import {
	ActiosnContainer,
	SectionLayout,
} from '@Pages/ClassRooms/Pages/AddClassroom/Components/AddClassroomBasicInfo/AddClassroomBasicInfo';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { Divider, FlexLayout, Spacer } from '@Styled/utilities';
import {
	Box,
	Button,
	Checkbox,
	MenuItem,
} from '@mui/material';
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation,
	useParams,
	useRouteMatch,
} from 'react-router-dom';
import styled from 'styled-components';
import useStandalonePageLoader from './useStandalonePageLoader';
import useAdminActionPermissions from './useAdminActionPermissions';
import CoursePositionField from '../ClassRooms/Pages/SingleCourse/Pages/CourseForm/Components/CoursePositionField';
import {
	clearContentApiScope,
	ContentModeProvider,
	setContentApiScope,
} from '@ContentManagement/ContentModeContext';

type Section = { id: number; name: string; order: number; courses?: Array<{ id: number }> };
type StandaloneTab = {
	id: number;
	name: string;
	order?: number;
	sections?: Section[];
};
/** a section as the lecture form's Section / Order fields see it */
type FormSection = Section & {
	tabName: string;
	classroomTab: { order?: number };
	courses_count: number;
};
type StandaloneCourse = {
	id: number;
	label: string;
	title: string;
	tabs?: StandaloneTab[];
};
type Lecture = {
	id: number;
	standalone_course_id: number;
	name: string;
	description?: string;
	code?: string;
	order: number | null;
	section_id?: number;
	sectionId?: number;
	section?: Section;
	preview_url?: string | null;
	active: boolean;
	active_start_date?: string | null;
	active_end_date?: string | null;
	expiry_date?: string | null;
	expiry_period?: number | null;
	skip_expiry_for_completed?: boolean;
	sequential_units?: boolean;
	units?: any[];
};

const responseData = <T,>(response: any): T => response.data.data as T;

const lectureRouteGroup = (isAdmin: boolean): RouteGroup => ({
	title: 'Lecture',
	route: 'lecture/:id',
	overviewRoute: 'edit/info',
	code: '',
	component: null,
	isRouted: true,
	isParent: true,
	routes: [
		{
			title: 'Users',
			route: 'users',
			icon: '',
			code: isAdmin ? 'v-enrl-cl' : 'course_content',
			component: null,
		},
		{
			title: 'Analytics',
			route: 'analytics',
			icon: '',
			code: isAdmin ? 'v-anlt-cr' : 'course_content',
			component: null,
		},
		{
			title: 'Edit Lecture',
			route: 'edit',
			icon: '',
			code: '',
			offMenu: true,
			component: null,
		},
	],
});

export const AddStandaloneLecture: React.FC = () => {
	const { standaloneLabel } = useParams<{ standaloneLabel: string }>();
	const [course, setCourse] = useState<StandaloneCourse>();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const { addLink, removeLink } = useContext(BreadcrumbContext);

	useEffect(() => {
		withLoading(async () => {
			const response = await api.post('standalone-courses', {
				label: standaloneLabel,
			});
			setCourse(responseData<StandaloneCourse>(response));
		})
			.catch((error) =>
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load standalone Course'
				)
			);
	}, [displaySnackbar, standaloneLabel, withLoading]);

	useEffect(() => {
		if (!course) return;
		addLink?.({
			route: `/dashboard/classrooms/standalone-courses/${course.label}`,
			title: course.title,
			routeHash: 'standalone-course',
		});
		addLink?.({
			route: `/dashboard/classrooms/standalone-courses/${course.label}/create/lecture/info`,
			title: 'Add Lecture',
			routeHash: 'add-lecture',
		});
		return () => {
			removeLink?.('add-lecture');
			removeLink?.('standalone-course');
		};
	}, [addLink, course, removeLink]);

	return course ? (
		<FlexLayout px="4rem">
			<FormContainerStyle>
				<LectureForm course={course} mode="add" />
			</FormContainerStyle>
		</FlexLayout>
	) : null;
};

export const StandaloneLectureManagement: React.FC = () => {
	const { standaloneLabel, id: lectureId } = useParams<{
		standaloneLabel: string;
		id: string;
	}>();
	const { path, url } = useRouteMatch();
	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const { setCurrentRouteGroup, setBaseURL } = useContext(NavMenuContext);
	const { addLink, removeLink } = useContext(BreadcrumbContext);
	const [course, setCourse] = useState<StandaloneCourse>();
	const [lecture, setLecture] = useState<Lecture>();
	const isAdmin = api.defaults.baseURL?.includes('/admin/') === true;
	const routeGroup = useMemo(() => lectureRouteGroup(isAdmin), [isAdmin]);

	useEffect(() => {
		const id = Number(lectureId);
		setContentApiScope({ mode: 'standalone-lecture', lectureId: id });
		return () => clearContentApiScope(id);
	}, [lectureId]);

	const load = useCallback(async () => {
		try {
			await withLoading(async () => {
				const [courseResponse, lectureResponse] = await Promise.all([
					api.post('standalone-courses', { label: standaloneLabel }),
					api.post('standalone-courses/lectures', { id: Number(lectureId) }),
				]);
				const nextCourse = responseData<StandaloneCourse>(courseResponse);
				const nextLecture = responseData<Lecture>(lectureResponse);
				if (nextLecture.standalone_course_id !== nextCourse.id) {
					throw new Error('Lecture does not belong to this standalone Course');
				}
				setCourse(nextCourse);
				setLecture(nextLecture);
				dispatch(setCurrentCourse(nextLecture as any));
			});
		} catch (error: any) {
			displaySnackbar(
				'error',
				error.response?.data?.message || error.message || 'Unable to load Lecture'
			);
		}
	}, [dispatch, displaySnackbar, lectureId, standaloneLabel, withLoading]);

	useEffect(() => {
		load();
		return () => {
			dispatch(resetCurrentCourse());
		};
	}, [dispatch, load]);

	useEffect(() => {
		setCurrentRouteGroup?.(routeGroup);
		setBaseURL?.(
			`/dashboard/classrooms/standalone-courses/${standaloneLabel}/lecture/${lectureId}`
		);
	}, [lectureId, routeGroup, setBaseURL, setCurrentRouteGroup, standaloneLabel]);

	useEffect(() => {
		if (!course || !lecture) return;
		const base = `/dashboard/classrooms/standalone-courses/${course.label}`;
		addLink?.({
			route: base,
			title: course.title,
			routeHash: 'standalone-course',
		});
		addLink?.({
			route: `${base}/lecture/${lecture.id}/edit/content`,
			title: lecture.name,
			routeHash: 'lecture',
		});
		return () => {
			removeLink?.('lecture');
			removeLink?.('standalone-course');
		};
	}, [addLink, course, lecture, removeLink]);

	if (!course || !lecture) return null;

	return (
		<ContentModeProvider
			scope={{ mode: 'standalone-lecture', lectureId: lecture.id }}
		>
			<CourseContext.Provider value={{ id: lecture.id, triggerUpdate: load }}>
				<div>
				<LectureTitleWrapper>
					<Spacer marginBottom="2.813rem" mx="2.125rem" />
					<ParentTitle>{course.title}</ParentTitle>
					<ChildTitle>{lecture.name}</ChildTitle>
				</LectureTitleWrapper>
				<Switch>
					<Route exact path={path}>
						<Redirect to={`${url}/edit/info`} />
					</Route>
					<Route path={`${path}/test/:id`}>
						<Test />
					</Route>
					<Route path={`${path}/edit`}>
						<LectureOverview course={course} lecture={lecture} reload={load} />
					</Route>
					<Route path={`${path}/users`}>
						<EnrollmentStudents />
					</Route>
					<Route path={`${path}/analytics`}>
						<StandaloneLectureAnalytics />
					</Route>
					<Redirect to={`${url}/edit/info`} />
				</Switch>
				</div>
			</CourseContext.Provider>
		</ContentModeProvider>
	);
};

const LectureTitleWrapper = styled.div`
	margin-top: 2.875rem;
	margin-bottom: 2.313rem;
	align-items: center;
	display: flex;
`;

const ParentTitle = styled.label`
	font-size: 1.25rem;
	font-weight: bold;
`;

const ChildTitle = styled(ParentTitle)`
	font-weight: 400;
	margin: 0 1rem;
`;

const LectureOverview: React.FC<{
	course: StandaloneCourse;
	lecture: Lecture;
	reload: () => void;
}> = ({ course, lecture, reload }) => {
	const { url } = useRouteMatch();
	const { pathname } = useLocation();
	const selected = pathname.includes('/edit/content')
		? 'content'
		: pathname.includes('/edit/rules-paths')
		? 'rules-paths'
		: pathname.includes('/edit/files')
		? 'files'
		: pathname.includes('/edit/reports')
		? 'reports'
		: 'info';

	return (
		<FlexLayout px="4rem">
			<FormContainerStyle>
				<RoutedTabs baseURL={url} withRouter width="100%">
					<RoutedTabs.Tabs
						onChange={() => {}}
						$hashPrefix="#"
						hashes={['info', 'content', 'rules-paths', 'files', 'reports']}
						orientation="vertical"
						value={selected}
					>
						<RoutedTabs.Item
							value="info"
							label={<RoutedTabs.ItemTitle>Basic Info</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon>menu_book</RoutedTabs.ItemIcon>}
						/>
						<RoutedTabs.Item
							value="content"
							label={<RoutedTabs.ItemTitle>Content</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon>content_paste</RoutedTabs.ItemIcon>}
						/>
						<RoutedTabs.Item
							value="rules-paths"
							label={<RoutedTabs.ItemTitle>Rules & Paths</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon>insights</RoutedTabs.ItemIcon>}
						/>
						<RoutedTabs.Item
							value="files"
							label={<RoutedTabs.ItemTitle>Files</RoutedTabs.ItemTitle>}
							icon={
								<RoutedTabs.ItemIcon mIconType="TwoTone">
									folder_open
								</RoutedTabs.ItemIcon>
							}
						/>
						<RoutedTabs.Item
							value="reports"
							label={<RoutedTabs.ItemTitle>Reports</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon mIconType="Outlined">summarize</RoutedTabs.ItemIcon>}
						/>
					</RoutedTabs.Tabs>
					<Spacer mx="1.25rem" />
					<RoutedTabs.Content value={selected}>
						<RoutedTabs.ContentItem hash="info">
							<LectureForm course={course} lecture={lecture} mode="edit" onSaved={reload} />
						</RoutedTabs.ContentItem>
					<RoutedTabs.ContentItem hash="content">
							<CourseContent />
						</RoutedTabs.ContentItem>
						<RoutedTabs.ContentItem hash="rules-paths">
							<StandaloneLectureRulesPaths
								standaloneCourseId={course.id}
								standaloneCourseLabel={course.label}
								lectureId={lecture.id}
							/>
						</RoutedTabs.ContentItem>
						<RoutedTabs.ContentItem hash="files">
							{/* ContentModeProvider above makes the axios interceptor
							    reroute video-import/* and inject standalone_lecture_id. */}
							<FilesTab courseId={lecture.id} />
						</RoutedTabs.ContentItem>
						<RoutedTabs.ContentItem hash="reports">
							<ReportsOverview />
						</RoutedTabs.ContentItem>
					</RoutedTabs.Content>
				</RoutedTabs>
			</FormContainerStyle>
		</FlexLayout>
	);
};

type LectureFormValues = Lecture & { Set_active_period: boolean };

const initialLecture = (
	course: StandaloneCourse,
	section?: FormSection
): LectureFormValues => ({
	id: 0,
	standalone_course_id: course.id,
	name: '',
	description: '',
	code: '',
	// last in the preselected section (was 1: every new lecture landed first)
	order: section ? section.courses_count + 1 : null,
	section_id: section?.id,
	active: true,
	active_start_date: null,
	active_end_date: null,
	expiry_date: null,
	expiry_period: null,
	skip_expiry_for_completed: false,
		Set_active_period: false,
});

const formLecture = (
	lecture: Lecture,
	course: StandaloneCourse
): LectureFormValues => ({
	...lecture,
	standalone_course_id: course.id,
	section_id: lecture.section_id || lecture.sectionId,
	active_start_date: lecture.active_start_date?.slice(0, 16) || null,
	active_end_date: lecture.active_end_date?.slice(0, 16) || null,
	expiry_date: lecture.expiry_date?.slice(0, 16) || null,
	Set_active_period: Boolean(
		lecture.active_start_date || lecture.active_end_date
	),
});

const LectureForm: React.FC<{
	course: StandaloneCourse;
	lecture?: Lecture;
	mode: 'add' | 'edit';
	onSaved?: () => void;
}> = ({ course, lecture, mode, onSaved }) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	// Lecture authoring is admin-only — in the instructor portal this form is
	// a read-only info sheet (the backend 403s the save routes regardless)
	const { isAdminPortal } = useAdminActionPermissions();
	const readOnly = !isAdminPortal;
	const sections = useMemo<FormSection[]>(
		() =>
			(course.tabs || []).flatMap((tab, tabIndex) =>
				(tab.sections || []).map((section) => ({
					...section,
					tabName: tab.name,
					classroomTab: { order: tab.order ?? tabIndex + 1 },
					courses_count: section.courses?.length ?? 0,
				}))
			),
		[course.tabs]
	);
	const [saving, setSaving] = useState(false);
	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { isDirty },
	} = useForm<LectureFormValues>({
		defaultValues: lecture
			? formLecture(lecture, course)
			: initialLecture(course, sections[0]),
		mode: 'all',
		reValidateMode: 'onChange',
	});

	useEffect(() => {
		reset(
			lecture
				? formLecture(lecture, course)
				: initialLecture(course, sections[0])
		);
	}, [course, lecture, reset, sections]);

	const save = async (draft: LectureFormValues) => {
		if (!draft.name.trim() || !draft.code?.trim() || (!draft.section_id && !draft.sectionId)) {
			displaySnackbar('error', 'Name, code, and section are required');
			return;
		}
		const order = Number(draft.order);
		if (!Number.isInteger(order) || order < 1) {
			displaySnackbar('error', 'Order must be a whole number, 1 or more');
			return;
		}
		setSaving(true);
		try {
			const payload = {
				id: lecture?.id,
				standalone_course_id: course.id,
				section_id: Number(draft.section_id || draft.sectionId),
				name: draft.name.trim(),
				code: draft.code?.trim(),
				description: draft.description || '',
				// position inside the section (the server shifts the others)
				order,
				preview_url: draft.preview_url || undefined,
				active: Boolean(draft.active),
				active_start_date: draft.Set_active_period
					? draft.active_start_date || null
					: null,
				active_end_date: draft.Set_active_period
					? draft.active_end_date || null
					: null,
				expiry_date: draft.expiry_date || null,
				expiry_period: draft.expiry_period ? Number(draft.expiry_period) : null,
				skip_expiry_for_completed: Boolean(draft.skip_expiry_for_completed),
			};
			const response = await withLoading(() =>
				api.request({
					method: mode === 'add' ? 'post' : 'put',
					url:
						mode === 'add'
							? 'standalone-courses/lectures/create'
							: 'standalone-courses/lectures/update',
					data: payload,
				})
			);
			const saved = responseData<Lecture>(response);
			dispatch(setCurrentCourse(saved as any));
			displaySnackbar(
				'success',
				mode === 'add' ? 'Lecture created successfully' : 'Lecture updated successfully'
			);
			if (mode === 'add') {
				history.replace(
					`/dashboard/classrooms/standalone-courses/${course.label}/lecture/${saved.id}/edit/info`
				);
			} else {
				onSaved?.();
			}
		} catch (error: any) {
			displaySnackbar(
				'error',
				error.response?.data?.message ||
					'Unable to save Lecture'
			);
		} finally {
			setSaving(false);
		}
	};
	const setActivePeriod = watch('Set_active_period');
	const cancel = () => {
		if (mode === 'edit' && lecture) {
			history.push(
				`/dashboard/classrooms/standalone-courses/${course.label}/lecture/${lecture.id}/edit/content`
			);
			return;
		}
		history.push(
			`/dashboard/classrooms/standalone-courses/${course.label}`
		);
	};

	return (
		<form onSubmit={handleSubmit(save)} style={{ width: '100%' }}>
			<SectionLayout
				gridGap={'2rem'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<TitleStyled>
					{mode === 'add' ? 'Add Lecture' : readOnly ? 'Lecture Info' : 'Edit Lecture'}
				</TitleStyled>
			</SectionLayout>
			<div style={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : undefined}>
			<SectionLayout
				gridGap={'2rem'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<EdFormControl control={control} required name="name" label="Name" />
				<EdFormControl control={control} select required name="section_id" label="Section">
					{sections.map((section) => (
						<MenuItem key={section.id} value={section.id}>
							{section.tabName} - {section.name}
						</MenuItem>
					))}
				</EdFormControl>
				<EdFormControl control={control} required name="code" label="Code" />
				<CoursePositionField
					control={control}
					watch={watch}
					setValue={setValue}
					sections={sections}
					savedSectionId={mode === 'edit' ? lecture?.section_id || lecture?.sectionId : undefined}
					savedOrder={mode === 'edit' ? lecture?.order : undefined}
				/>
				<EdFormControl control={control} name="preview_url" label="Preview" />
				<EdFormControl control={control} multiline name="description" label="Description" />
			</SectionLayout>
			<Divider />
			<SectionLayout
				gridGap={'2rem'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
					}}
				>
					<EdFormControl
						control={control}
						switch
						checked={watch('active')}
						name="active"
						label="Active"
						disabled={setActivePeriod}
					/>
					<Controller
						name="Set_active_period"
						control={control}
						render={({ field }) => (
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Checkbox checked={Boolean(field.value)} {...field} />
								<label>Set Period</label>
							</Box>
						)}
					/>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					{setActivePeriod && (
						<>
							<EdFormControl
								width="16rem"
								type="datetime-local"
								control={control}
								name="active_start_date"
								label="start"
								sx={{ marginBottom: '0.5rem' }}
							/>
							<EdFormControl
								width="16rem"
								type="datetime-local"
								control={control}
								name="active_end_date"
								label="end"
								sx={{ marginTop: '0.5rem' }}
							/>
						</>
					)}
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<EdFormControl
						control={control}
						width="4.75rem"
						type="number"
						inputMode="numeric"
						name="expiry_period"
						label="Expiry duration"
						inputProps={{ min: 1 }}
					/>
					<SpanStyle>Days</SpanStyle>
				</Box>
				<EdFormControl
					width="16rem"
					type="datetime-local"
					control={control}
					name="expiry_date"
					label="Expiry date"
				/>
				<EdFormControl
					control={control}
					switch
					name="skip_expiry_for_completed"
					label="Skip Expiry For Completed"
				/>
			</SectionLayout>
			</div>
			{!readOnly && (
				<ActiosnContainer>
					<Button
						type="button"
						variant="contained"
						color="warning"
						onClick={cancel}
					>
						Cancel
					</Button>
					<Spacer mx={'.5rem'} />
					<Button
						type="submit"
						variant="contained"
						disabled={saving || !isDirty}
					>
						Save
					</Button>
				</ActiosnContainer>
			)}
		</form>
	);
};
export default StandaloneLectureManagement;
